import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

/** Retries for a GET that failed for a reason worth trying again (transport error or 5xx). */
export const DEFAULT_MAX_RETRIES = 3;

export const isUnauthorized = (error: FetchBaseQueryError | undefined): boolean =>
  error?.status === 401 || (error?.status === 'PARSING_ERROR' && error.originalStatus === 401);

/** True for a definite "this request is wrong" answer, which retrying cannot change. */
export const isClientError = (error: FetchBaseQueryError | undefined): boolean => {
  if (!error) return false;
  if (typeof error.status === 'number') return error.status >= 400 && error.status < 500;
  if (error.status === 'PARSING_ERROR') return error.originalStatus >= 400 && error.originalStatus < 500;
  return false;
};

export interface RetryDecisionInput {
  error: FetchBaseQueryError | undefined;
  /** 1 for the first failure, 2 for the second, and so on. */
  attempt: number;
  requestType: 'query' | 'mutation';
  /** Per-endpoint `extraOptions.maxRetries`, when the endpoint sets one. */
  maxRetries?: number;
}

/**
 * Retry only when another attempt could succeed and is safe. Mutations are never retried: the
 * server may have already applied them, so a retry can create duplicates.
 */
export const shouldRetryRequest = ({ error, attempt, requestType, maxRetries }: RetryDecisionInput): boolean => {
  if (attempt > (maxRetries ?? DEFAULT_MAX_RETRIES)) return false;
  if (requestType === 'mutation') return false;
  return !isClientError(error);
};
